import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { FilterMatchMode } from 'primereact/api';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { useAuth } from '../contexts/AuthContext.js';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Paginator } from 'primereact/paginator';
import { Calendar } from 'primereact/calendar';
import '../config.js';
import { ScrollTop } from 'primereact/scrolltop';
import useStatusPageScript from '../hooks/useStatusPageScript.js'; 
import { ProgressBar } from 'primereact/progressbar';
import Login from './Login.js';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import supportTileLogsApi from '../api/supportTileLogsApi.js';

const SupportLogs = () => {
  // Load the status page script
  useStatusPageScript();
  
  const { id } = useParams();
  const [events, setEvents] = useState([]);
  const navigate = useNavigate();
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const { logout } = useAuth();
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    number: { value: null, matchMode: FilterMatchMode.CONTAINS },
    scanType: { value: null, matchMode: FilterMatchMode.CONTAINS},
    time: { value: null, matchMode: FilterMatchMode.CONTAINS},
    allowed: {value: null, matchMode: FilterMatchMode.CONTAINS},
    'accessPoint.client.name': { value: null, matchMode: FilterMatchMode.CONTAINS},
    'accessPoint.gate': { value: null, matchMode: FilterMatchMode.CONTAINS},
  });
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(20);
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [membership, setMembership] = useState('');
  const [appliedMembership, setAppliedMembership] = useState('');
  const [, setAppId] = useState(0);
  const [useAppId, setUseAppId] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const toast = useRef(null);

    const handleLoginSuccess = () => {
        setShowLoginModal(false);
        if (toast.current) {
            toast.current.show({severity: 'success', summary: '', detail: 'Login successful! You can continue working.'});
        }
        getEvents(rows, 0);
    };

  useEffect(() => {
    const fetchEvents = async () => {
        getEvents(rows,0);
    };

    let appid = localStorage.getItem('appid');
    if(appid !== null){
      setAppId(appid);
      setUseAppId(true);
    }

    fetchEvents();
  }, [id]);

  const formatQueryDate = (value) => new Date(value).toLocaleString('sv').replace(' ', 'T').split('T')[0];

  const getQueryDateRange = () => {
    const [start, end] = Array.isArray(dateRange) ? dateRange : [];
    const startDate = start || new Date();
    const endDate = end || startDate;

    return {
      initDate: formatQueryDate(startDate),
      endDate: formatQueryDate(endDate),
    };
  };

  const getEvents = async (rows, page, membershipFilter = appliedMembership) => {
    try {
        setLoading(true);
        setEvents([]);
        const { initDate, endDate } = getQueryDateRange();
        const tile = localStorage.getItem('tilelog');
        const customerId = localStorage.getItem('id');
        let appid = localStorage.getItem('appid');

        const payload = {
          tileId: tile,
          customerId: Number(customerId),
          query: {
            initDate,
            endDate,
            page,
            pageSize: rows,
            membership: membershipFilter.trim() || undefined,
          },
        };

        const responseData = await supportTileLogsApi.getSupportTileLogs(payload, appid);
        const formattedEvents = (Array.isArray(responseData) ? responseData : []).map((item) => {
          const rawTime = item && item.time;
          if (!rawTime) {
            return item;
          }

          const date = new Date(`${rawTime}Z`);
          return {
            ...item,
            time: date.toLocaleString('en-GB'),
          };
        });

        const responseCount = await supportTileLogsApi.getSupportTileLogsCount(
          {
            ...payload,
            query: {
              initDate,
              endDate,
              membership: membershipFilter.trim() || undefined,
            },
          },
          appid,
        );
          // let total = 0;
          // if(response.data.length == rows){
          //   total = (page + 1) * rows +1;
          // } else {
          //   total = page * rows + response.data.length;
          // }
          // setTotalRecords(total);
        setEvents(formattedEvents);
        setTotalRecords(responseCount);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching event record:', error);
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            setShowLoginModal(true);
          }
          setLoading(false);
          setTotalRecords(0);
          setEvents([]);
      }
    };

  const onPageChange = (event) => {
    setLoading(true);
    setRows(event.rows);
    getEvents(event.rows, event.page, appliedMembership);
    setFirst(event.first);
  };
  
  const getSeverity = (log) => {
    switch (log.allowed) {
        case true:
            return 'success';

        case false:
            return 'danger';

        default:
            return null;
    }
};

const statusBodyTemplate = (log) => {
  let val = log.allowed ? 'Allowed' : 'Denied';
    return <Tag value={val} severity={getSeverity(log)}></Tag>;
};

  const getByDate = () =>{
    const nextMembership = membership.trim();
    setAppliedMembership(nextMembership);
    setLoading(true);
    setFirst(0);
    getEvents(rows, 0, nextMembership)
  }

  const clearFilters = () => {
    setMembership('');
    setAppliedMembership('');
    setDateRange([new Date(), new Date()]);
    setFirst(0);
    setLoading(true);
    getEvents(rows, 0, '');
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };

    _filters['global'].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const progressbar = (        
    <div className="card mt-3">
        <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
    </div>);

  const membershipBodyTemplate = (rowData) => {
    const value = String(rowData.number || '');
    const query = appliedMembership.trim();
    if (!query) {
      return value;
    }

    const lowerValue = value.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const start = lowerValue.indexOf(lowerQuery);
    if (start < 0) {
      return value;
    }

    const end = start + query.length;
    return (
      <>
        {value.slice(0, start)}
        <mark>{value.slice(start, end)}</mark>
        {value.slice(end)}
      </>
    );
  };

  const logoutUser = async () => {
    await logout();
    navigate('/login');
  };

  const escapeCsv = (value) => {
    const safeValue = value === null || value === undefined ? '' : value;
    const text = String(safeValue);
    return `"${text.replace(/"/g, '""')}"`;
  };

  const toSafeFilePart = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    return raw.replace(/[^a-zA-Z0-9-_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').toLowerCase();
  };

  const downloadCsv = () => {
    if (!Array.isArray(events) || events.length === 0) {
      if (toast.current) {
        toast.current.show({ severity: 'warn', summary: '', detail: 'No logs available to export.' });
      }
      return;
    }

    const headers = ['Membership', 'Allowed', 'Time', 'Access Point', 'Scan Type', 'Client'];
    const rowsCsv = events.map((item) => {
      const accessPoint = item && item.accessPoint ? item.accessPoint : null;
      const client = accessPoint && accessPoint.client ? accessPoint.client : null;
      const row = [
        item && item.number,
        item && item.allowed === true ? 'Allowed' : item && item.allowed === false ? 'Denied' : '',
        item && item.time,
        accessPoint && accessPoint.gate,
        item && item.scanType,
        client && client.name,
      ];
      return row.map(escapeCsv).join(',');
    });

    const csvContent = [headers.map(escapeCsv).join(','), ...rowsCsv].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');

    const firstItem = events.length > 0 ? events[0] : null;
    const firstAccessPoint = firstItem && firstItem.accessPoint ? firstItem.accessPoint : null;
    const firstClient = firstAccessPoint && firstAccessPoint.client ? firstAccessPoint.client : null;
    const tileName = localStorage.getItem('tilelog');
    const locationName = localStorage.getItem('supportsite');
    const clientName = firstClient && firstClient.name ? firstClient.name : localStorage.getItem('supportclient');
    const accessPointName = firstAccessPoint && firstAccessPoint.gate ? firstAccessPoint.gate : '';
    const contextPart = toSafeFilePart(tileName) || toSafeFilePart(accessPointName) || toSafeFilePart(locationName) || toSafeFilePart(clientName) || 'results';

    a.href = url;
    a.download = `support-tile-logs-${contextPart}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Card className='justify-content-md-center mt-5'>
      <Container fluid="md" >
      <div className="panel">
        <div className="flex align-content-center flex-wrap">
          <Button link icon="pi pi-arrow-left" onClick={() => navigate(-1)} className='flex mb-3'/> 
          <h4 className='flex mt-2 mb-3'>Logs for  tile {localStorage.getItem('tilelog')}</h4> 
          <span className="p-input-icon-left mb-3 ml-3">
            <i className="pi pi-search" />
            <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Filter Search" tooltip="Start typing to filter" 
              tooltipOptions={{position: "bottom"}}/>
          </span>
          <div className="flex align-items-center ml-3 mb-3" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
            <div className="card flex justify-content-center">
              <Calendar value={dateRange} onChange={(e) => { setDateRange(e.value || [new Date(), new Date()]); setFirst(0); }} selectionMode="range" dateFormat="dd/mm/yy" showIcon  showButtonBar readOnlyInput />
            </div>
            <span className="p-input-icon-left">
              <i className="pi pi-id-card" />
              <InputText value={membership} onChange={(e) => setMembership(e.target.value)} placeholder="Member Number" tooltip="Filter by member number" tooltipOptions={{position: "bottom"}}/>
            </span>
            <div className="card flex justify-content-center">
              <Button label="Get" className="p-button-raised" onClick={getByDate} />
            </div>
          </div>
          <div className="card flex justify-content-center ml-3 mb-3">
            <Button icon="pi pi-times" className="p-button-outlined" onClick={clearFilters} disabled={loading} tooltip="Clear filters" tooltipOptions={{position: "bottom"}} />
          </div>
          <div className="card flex justify-content-center ml-3 mb-3">
            <Button icon="pi pi-download" className="p-button-outlined" onClick={downloadCsv} disabled={loading || events.length === 0} tooltip="Download current results as CSV" tooltipOptions={{position: "bottom"}} />
          </div>
        </div>

        {appliedMembership.trim() && (
          <div className="ml-3 mb-2">
            <small>Active Member Number filter: {appliedMembership.trim()}</small>
          </div>
        )}
        {/* <Toolbar           
        start={

        } 
        end = {        
          }/> */}
      </div>
      {loading && progressbar}
        {/* <Row className='justify-content-md-center mt-5'>
          <Col sm={8}>

          </Col>
          <Col sm={4}>

          </Col>
        </Row> */}
    {/* <Row className='justify-content-md-center'>
        <div className="card">
            <Paginator first={first} rows={rows} totalRecords={1000} rowsPerPageOptions={[10, 20, 30]} onPageChange={onPageChange} />
        </div>
    </Row> */}
{/* 
        <Row className='justify-content-md-center mt-5'>
            <div className="card">
                <Paginator first={first} rows={rows} totalRecords={totalRecords} rowsPerPageOptions={[20, 30, 40, 50, 75, 100]} onPageChange={onPageChange} />
            </div>
        </Row> */}
        <Toast ref={toast} />
        <Row className='justify-content-md-center mt-5'>
            <Col>
            <DataTable value={events} selectionMode={'radiobutton'} selection={rowSelectionModel} onSelectionChange={(e) => setRowSelectionModel(e.value.id)} 
            dataKey="id" tableStyle={{ minWidth: '50rem' }} filters={filters} filterDisplay="menu" emptyMessage={loading ? "Loading logs..." : (appliedMembership.trim() ? "No logs found for the selected date range and member number." : "No logs found.")}
            globalFilterFields={['number',  'allowed', 'time', 'accessPoint.gate', 'scanType', 'accessPoint.client.name']}  
            scrollable scrollHeight="60vh">
              <Column field="number" body={membershipBodyTemplate} header="Membership" showFilterMenu={false} filterMenuStyle={{ width: '14rem' }} style={{ minWidth: '12rem' }} ></Column>
                <Column field="allowed" body={statusBodyTemplate} showFilterMenu={true} filterMenuStyle={{ width: '14rem' }} style={{ minWidth: '12rem' }}></Column>
                <Column field="time" header="Time"></Column>
                <Column field="accessPoint.gate" header="Access Point"></Column>
                <Column field="scanType" header="Scan Type"></Column>
                <Column field="accessPoint.client.name" header="Client"></Column>
            </DataTable>
            </Col>

        </Row>

        <Row className='justify-content-md-center mt-5'>
            <div className="card">
                <Paginator first={first} rows={rows} totalRecords={totalRecords} rowsPerPageOptions={[20, 30, 40, 50, 75, 100]} onPageChange={onPageChange} />
            </div>
        </Row>
        <div className="card flex justify-content-center">
          <Dialog 
            header="Session Expired - Please Login Again" 
            visible={showLoginModal} 
            style={{ width: '450px' }} 
            modal={true}
            closable={false}
            className="justify-content-center"
          >
            <p className="mb-3">Your session has expired. Please log in again to continue working without losing your form data.</p>
            <div className="flex justify-content-center">
              <Login onLoginSuccess={handleLoginSuccess} />
            </div>
          </Dialog>
        </div>
      </Container>
    <ScrollTop />
    </Card>
  );
};

export default SupportLogs;
